import { FetchQueryOptions } from "react-query";
import { AxiosRequestConfig, AxiosRequestHeaders } from "axios";
import { message } from "antd";
import { filterParams, setFormData } from "./helpers.utils";
import httpBase from "./axios.util";

class Http {
  /**
   * GET REQUEST
   * @param url
   * @param params
   * @param responseHeader
   * @param additionalParams
   * @param additionalHeaders
   * @param additionalAxiosOptions
   */
  async get(
    url: string,
    params: FetchQueryOptions | null = null,
    additionalParams: {} = {},
    responseHeader: boolean = false,
    additionalHeaders: AxiosRequestHeaders = {},
    additionalAxiosOptions: AxiosRequestConfig = {}
  ) {
    params = filterParams(
      params && params?.queryKey ? { ...(params.queryKey[0] as object) } : {}
    );

    return await httpBase(
      responseHeader,
      additionalAxiosOptions,
      additionalHeaders
    ).get(url, {
      params: {
        ...params,
        ...additionalParams,
      },
    });
  }

  /**
   * POST REQUEST
   * @param url
   * @param data
   * @param responseHeader
   * @param additionalHeaders
   * @param additionalAxiosOptions
   */
  async post<T extends Object>(
    url: string,
    data: T,
    multipart?: boolean,
    responseHeader: boolean = false,
    additionalHeaders: AxiosRequestHeaders = {},
    additionalAxiosOptions: AxiosRequestConfig = {}
  ) {
    let formData;
    if (multipart) {
      formData = setFormData(filterParams(data));
    } else {
      formData = data;
    }
    return await httpBase(
      responseHeader,
      additionalAxiosOptions,
      additionalHeaders
    ).post(url, formData, {
      headers: {
        "Content-Type": `${
          multipart ? "multipart/form-data" : "application/json"
        }`,
      },
    });
  }

  /**
   * PATCH REQUEST
   * @param url
   * @param identifier
   * @param data
   * @param responseHeader
   * @param additionalHeaders
   * @param additionalAxiosOptions
   */
  async patch<T extends Object>(
    url: string,
    identifier: number | string,
    data: T,
    multipart?: boolean,
    responseHeader: boolean = false,
    additionalHeaders: AxiosRequestHeaders = {},
    additionalAxiosOptions: AxiosRequestConfig = {}
  ) {
    let formData;
    if (multipart) {
      formData = setFormData(filterParams(data));
    } else {
      formData = data;
    }
    return await httpBase(
      responseHeader,
      additionalAxiosOptions,
      additionalHeaders
    ).patch(`${url}/${identifier}`, formData, {
      headers: {
        "Content-Type": `${
          multipart ? "multipart/form-data" : "application/json"
        }`,
      },
    });
  }

  /**
   * PUT REQUEST
   * @param url
   * @param identifier
   * @param data
   * @param responseHeader
   * @param additionalHeaders
   * @param additionalAxiosOptions
   */
  async put<T extends Object>(
    url: string,
    identifier: number | string,
    data: T,
    multipart?: boolean,
    responseHeader: boolean = false,
    additionalHeaders: AxiosRequestHeaders = {},
    additionalAxiosOptions: AxiosRequestConfig = {}
  ) {
    let formData;
    if (multipart) {
      formData = setFormData(filterParams(data));
    } else {
      formData = data;
    }
    return await httpBase(
      responseHeader,
      additionalAxiosOptions,
      additionalHeaders
    ).put(`${url}/${identifier}`, formData, {
      headers: {
        "Content-Type": `${
          multipart ? "multipart/form-data" : "application/json"
        }`,
      },
    });
  }

  /**
   * DELETE REQUEST
   * @param url
   * @param identifier
   * @param responseHeader
   * @param additionalHeaders
   * @param additionalAxiosOptions
   */
  async delete(
    url: string,
    identifier: string | number,
    responseHeader: boolean = false,
    additionalHeaders: AxiosRequestHeaders = {},
    additionalAxiosOptions: AxiosRequestConfig = {}
  ) {
    return await httpBase(
      responseHeader,
      additionalAxiosOptions,
      additionalHeaders
    ).delete(`${url}/${identifier}`);
  }
}

/**
 *
 * @param exception
 * @param notify
 * @param errorMessage
 */
export const handleError = (
  exception: any,
  notify = true,
  errorMessage = ""
) => {
  if (exception && exception.response && exception.response.data) {
    const error = exception.response.data;
    if (error.errors && Object.keys(error.errors).length) {
      Object.keys(error.errors).forEach((key) => {
        if (notify) message.error(error.errors[key][0]);
      });
      return error.errors;
    } else if (error.message) {
      return error.message;
    }
  } else {
    if (notify) message.error(errorMessage || "Something went wrong");
    return "Something went wrong";
  }
};

export default new Http();
